"use client";

import { Command } from "cmdk";
import { Book, CircleHelp, Clock, Search } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSearch } from "@/features/search/hooks/use-search";
import type { SearchContext } from "@/features/search/types";
import { useRecentSearches } from "@/lib/hooks/use-recent-searches";
import { cn, getSlug } from "@/lib/utils";
import { useI18n } from "@/locales/client";

interface SearchCommandProps {
  context: SearchContext;
  studySetId?: string;
  inline?: boolean;
  autoFocus?: boolean;
  onSelect?: () => void;
}
interface StudySet {
  id: string;
  name: string;
}

interface Question {
  id: string;
  question: string;
  studySetId: string;
  studySetName: string;
}

type _SearchResult = {
  studySets?: StudySet[];
  questions?: Question[];
};

export function SearchCommand({
  context,
  studySetId,
  inline = false,
  autoFocus = false,
  onSelect,
}: SearchCommandProps) {
  const t = useI18n();
  const [open, setOpen] = useState(inline);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [value, setValue] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const deferedValue = useDeferredValue(value);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    setValue("");
  }, [pathname]);

  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (open && !inline) {
      updateDropdownPosition();
    }
  }, [open, inline, updateDropdownPosition]);

  useEffect(() => {
    if (autoFocus) {
      // Delay focus so it happens after the drawer/dialog mount animation,
      // otherwise mobile keyboards may not open reliably.
      const id = window.setTimeout(() => inputRef.current?.focus(), 100);
      return () => window.clearTimeout(id);
    }
  }, [autoFocus]);

  const { recentSearches, addRecentSearch } = useRecentSearches();

  const { data, isLoading } = useSearch({
    q: deferedValue,
    context,
    studySetId,
    enabled: deferedValue.length > 0,
  });

  const getPlaceholderText = () => {
    if (studySetId) {
      return t("searchCommand.placeholder.question");
    }
    return t("searchCommand.placeholder.default");
  };

  const handleSelect = (item: StudySet | Question) => {
    if (value) {
      addRecentSearch(value);
    }

    if ("studySetId" in item) {
      if (pathname.includes("/edit")) {
        router.push(`/studysets/${item.studySetId}/edit?scrollTo=${item.id}`);
      } else {
        router.push(
          `/studysets/${getSlug(item.studySetName, item.studySetId)}?scrollTo=${item.id}`,
        );
      }
    } else if ("name" in item) {
      router.push(`/studysets/${getSlug(item.name, item.id)}`);
    }
    if (!inline) {
      setOpen(false);
    }
    inputRef.current?.blur(); // This removes focus from the input field
    onSelect?.();
  };

  const handleRecentSearch = useCallback(
    (search: string) => {
      setIsSelecting(true);
      addRecentSearch(search);
      setValue(search);

      // Use RAF to ensure we maintain focus after the selection
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        setIsSelecting(false);
      });
    },
    [addRecentSearch],
  );

  // Handle blur with proper focus checking
  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      // In inline mode (e.g. inside a drawer) the list is always visible, so
      // don't toggle `open` on blur.
      if (inline) return;

      // Get the related target (element that will receive focus)
      const relatedTarget = e.relatedTarget as HTMLElement;

      // Check if we're clicking inside the Command.List
      const isClickingList = relatedTarget?.closest("[cmdk-list]");

      // Only close if we're not clicking inside the list and not in selection
      if (!isClickingList && !isSelecting) {
        setTimeout(() => {
          setOpen(false);
        }, 200);
      }
    },
    [isSelecting, inline],
  );

  const hasNoResults =
    !isLoading &&
    value.length > 0 &&
    (!data || (!data.studySets?.length && !data.questions?.length));

  const listClassName = inline
    ? "max-h-full flex-1 overflow-y-auto overflow-x-hidden"
    : "max-h-[300px] overflow-y-auto overflow-x-hidden rounded-md border bg-popover shadow-md";

  const listContent = (
    <Command.List className={listClassName}>
      {value.length === 0 ? (
        <Command.Group
          heading={t("searchCommand.recentSearches")}
          className="overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
        >
          {recentSearches.map((search, index) => (
            <Command.Item
              key={search}
              value={`recent-${index}`}
              onSelect={() => handleRecentSearch(search)}
              className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
            >
              <Clock className="mr-2 h-4 w-4" />
              {search}
            </Command.Item>
          ))}
        </Command.Group>
      ) : isLoading ? (
        <div className="py-6 text-center text-sm">
          {t("searchCommand.searching")}
        </div>
      ) : (
        <>
          {data?.studySets && data.studySets.length > 0 && (
            <Command.Group
              heading={t("searchCommand.studySets")}
              className="overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
            >
              {data.studySets.map((set) => (
                <Command.Item
                  key={set.id}
                  value={`studyset-${set.id}`}
                  onSelect={() => handleSelect(set)}
                  className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
                >
                  <Book className="mr-2 h-4 w-4" />
                  {set.name}
                </Command.Item>
              ))}
            </Command.Group>
          )}
          {data?.questions && data.questions.length > 0 && (
            <Command.Group
              heading={t("searchCommand.questions")}
              className="overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
            >
              {data.questions.map((question) => (
                <Command.Item
                  key={question.id}
                  value={`question-${question.id}`}
                  onSelect={() => handleSelect(question)}
                  className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
                >
                  <CircleHelp className="mr-2 h-4 w-4" />
                  {question.question}
                </Command.Item>
              ))}
            </Command.Group>
          )}
          {hasNoResults ? (
            <div className="py-6 text-center text-sm">
              {t("searchCommand.noResults")}
            </div>
          ) : (
            <Command.Item
              onSelect={() => {
                if (studySetId) {
                  router.push(`${pathname}?q=${encodeURIComponent(value)}`);
                } else {
                  router.push(
                    `/search?q=${encodeURIComponent(value)}&type=studysets`,
                  );
                }
                if (!inline) {
                  setOpen(false);
                }
                inputRef.current?.blur();
                onSelect?.();
              }}
              className="relative flex cursor-default select-none items-center rounded-sm px-3 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
            >
              {t("searchCommand.viewAll")}
            </Command.Item>
          )}
        </>
      )}
    </Command.List>
  );

  return (
    <div className={cn("relative w-full", inline && "flex h-full flex-col")}>
      <Command
        shouldFilter={false} // Since we're handling filtering ourselves
        className={cn(
          "flex w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
          inline && "h-full",
        )}
      >
        <div className="relative flex items-center">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none size-4" />
          <Command.Input
            ref={inputRef}
            value={value}
            onValueChange={setValue}
            onFocus={() => {
              setOpen(true);
              if (!inline) {
                updateDropdownPosition();
              }
            }}
            onBlur={handleBlur}
            placeholder={getPlaceholderText()}
            className="flex h-11 w-full rounded-md py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 pl-10 pr-4 border border-input bg-muted"
          />
        </div>
        {inline ? (
          <div className="mt-3 flex-1 overflow-hidden">{listContent}</div>
        ) : (
          open &&
          typeof window !== "undefined" &&
          createPortal(
            <div
              className="fixed z-[9999]"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
              }}
              onMouseDown={(e) => {
                // Prevent blur when clicking the dropdown
                e.preventDefault();
              }}
            >
              {listContent}
            </div>,
            document.body,
          )
        )}
      </Command>
    </div>
  );
}
